/**
 * Base AI Provider Interface
 * Abstract class defining the contract for all AI providers
 */
export class BaseAIProvider {
  constructor(config = {}) {
    this.config = config;
    this.apiKey = config.apiKey;
    this.model = config.model;
    this.baseUrl = config.baseUrl;
  }

  /**
   * Get provider name
   * @returns {string}
   */
  getProviderName() {
    throw new Error('getProviderName() must be implemented by provider');
  }

  /**
   * Get model name
   * @returns {string}
   */
  getModelName() {
    return this.model || 'unknown';
  }

  /**
   * Check if provider is configured and ready
   * @returns {boolean}
   */
  isConfigured() {
    return !!(this.apiKey && this.model);
  }

  /**
   * Validate configuration
   * @throws {Error} if configuration is invalid
   */
  validateConfig() {
    if (!this.apiKey || this.apiKey === 'YOUR_API_KEY') {
      throw new Error(`${this.getProviderName()} API key is not configured`);
    }
    if (!this.model) {
      throw new Error(`${this.getProviderName()} model is not specified`);
    }
  }

  /**
   * Analyze CV content and extract structured data
   * @param {string} cvText - Raw CV text
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - Structured CV data
   */
  async analyzeCV(cvText, options = {}) {
    throw new Error('analyzeCV() must be implemented by provider');
  }

  /**
   * Analyze immigration profile based on CV and user data
   * @param {Object} userProfile - User wizard data
   * @param {Object} extractedData - Extracted CV data
   * @param {string} cvText - Raw CV text
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - Immigration analysis
   */
  async analyzeImmigrationProfile(userProfile, extractedData, cvText, options = {}) {
    throw new Error('analyzeImmigrationProfile() must be implemented by provider');
  }

  /**
   * Check if provider supports streaming responses
   * @returns {boolean}
   */
  supportsStreaming() {
    return false;
  }

  /**
   * Get rate limits for this provider
   * @returns {Object} - Rate limit information
   */
  getRateLimits() {
    return {
      requestsPerMinute: 60,
      tokensPerMinute: 60000,
      requestsPerDay: 1000
    };
  }

  /**
   * Get pricing information
   * @returns {Object} - Pricing per million tokens
   */
  getPricing() {
    return {
      inputCostPerMillion: 0,
      outputCostPerMillion: 0,
      currency: 'USD'
    };
  }

  /**
   * Health check for provider availability
   * @returns {Promise<boolean>}
   */
  async healthCheck() {
    try {
      this.validateConfig();
      return true;
    } catch (error) {
      console.error(`[${this.getProviderName()}] Health check failed:`, error);
      return false;
    }
  }

  /**
   * Normalize provider-specific response to common format
   * @param {Object} response - Provider-specific response
   * @returns {Object} - Normalized response
   */
  normalizeResponse(response) {
    return response;
  }

  /**
   * Handle provider-specific errors
   * @param {Error} error - Original error
   * @returns {Error} - Normalized error
   */
  handleError(error) {
    const normalizedError = new Error(error.message);
    normalizedError.provider = this.getProviderName();
    normalizedError.originalError = error;
    return normalizedError;
  }
}

export default BaseAIProvider;
